import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import SuperAdmin from "./pages/SuperAdmin";
import PatientDetail from "./pages/PatientDetail";
import PrintPrescription from "./pages/PrintPrescription";
import CertificateForm from "./pages/CertificateForm";
import PrintCertificate from "./pages/PrintCertificate";
import DietHub from "./pages/DietHub";
import PrintDietPlan from "./pages/PrintDietPlan";
import ExerciseHub from "./pages/ExerciseHub";
import PrintExercisePlan from "./pages/PrintExercisePlan";
import EducationHub from "./pages/EducationHub";
import PrintEducationArticle from "./pages/PrintEducationArticle";
import VerifyRx from "./pages/VerifyRx";
import GLP1CompanionKit from "./pages/GLP1CompanionKit";
import EduKitPage from "./pages/EduKitPage";
import Templates from "./pages/Templates";
import Network from "./pages/Network";
import BulkImport from "./pages/BulkImport";
import ReferralPrint from "./pages/ReferralPrint";
import Drafts from "./pages/Drafts";
import Reminders from "./pages/Reminders";
import Refills from "./pages/Refills";
import Portal from "./pages/Portal";
import Translations from "./pages/Translations";
import GuidelineSettings from "./pages/GuidelineSettings";
import LabPackageSettings from "./pages/LabPackageSettings";
import Reception from "./pages/Reception";
import StaffSettings from "./pages/StaffSettings";
import Analytics from "./pages/Analytics";
import AuditLog from "./pages/AuditLog";
import PitchPage, { PublicPitch } from "./pages/PitchPage";
import ClinicProfile from "./pages/ClinicProfile";
import DietConditionDefaults from "./pages/DietConditionDefaults";
import MedicineManager from "./pages/MedicineManager";
import { bootstrapClinic } from "./lib/clinic";

// Bootstrap the clinic profile once on module load so header/logo/prints
// show operator-configured branding from the very first render. Silent on
// failure — the neutral Prescribify fallback values apply.
bootstrapClinic();

function Protected({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;
  // Reception role is locked to the /reception intake page for safety —
  // clinical routes redirect them back so they cannot stumble into
  // diagnosis, medicines, or overrides.
  if (user.role === "reception" && (!allowedRoles || !allowedRoles.includes("reception"))) {
    return <Navigate to="/reception" replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/superadmin" element={<Protected><SuperAdmin /></Protected>} />
      <Route path="/patient/:pid" element={<Protected><PatientDetail /></Protected>} />
      <Route path="/print/:rxId" element={<Protected><PrintPrescription /></Protected>} />
      <Route path="/certificates/new/:pid/:type" element={<Protected><CertificateForm /></Protected>} />
      <Route path="/certificate/print/:cid" element={<Protected><PrintCertificate /></Protected>} />
      <Route path="/diet/:pid" element={<Protected><DietHub /></Protected>} />
      <Route path="/settings/diet-defaults" element={<Protected><DietConditionDefaults /></Protected>} />
      <Route path="/diet/print/:planId" element={<Protected><PrintDietPlan /></Protected>} />
      <Route path="/exercise/:pid" element={<Protected><ExerciseHub /></Protected>} />
      <Route path="/exercise/print/:planId" element={<Protected><PrintExercisePlan /></Protected>} />
      <Route path="/education" element={<Protected><EducationHub /></Protected>} />
      <Route path="/education/print/:aid" element={<Protected><PrintEducationArticle /></Protected>} />
      <Route path="/templates" element={<Protected><Templates /></Protected>} />
      <Route path="/network" element={<Protected><Network /></Protected>} />
      <Route path="/admin/import" element={<Protected><BulkImport /></Protected>} />
      <Route path="/referral/:refId" element={<Protected><ReferralPrint /></Protected>} />
      <Route path="/drafts" element={<Protected><Drafts /></Protected>} />
      <Route path="/reminders" element={<Protected><Reminders /></Protected>} />
      <Route path="/refills" element={<Protected><Refills /></Protected>} />
      <Route path="/portal/:token" element={<Portal />} />
      <Route path="/verify-rx/:token" element={<VerifyRx />} />
      <Route path="/glp1/kit/:kid" element={<Protected><GLP1CompanionKit /></Protected>} />
      <Route path="/edukit/:kid" element={<Protected><EduKitPage /></Protected>} />
      <Route path="/translations" element={<Protected><Translations /></Protected>} />
      <Route path="/settings/guidelines" element={<Protected><GuidelineSettings /></Protected>} />
      <Route path="/settings/lab-packages" element={<Protected><LabPackageSettings /></Protected>} />
      <Route path="/settings/staff" element={<Protected><StaffSettings /></Protected>} />
      <Route path="/settings/medicines" element={<Protected allowedRoles={["doctor","admin"]}><MedicineManager /></Protected>} />
      <Route path="/settings/clinic" element={<Protected allowedRoles={["doctor","admin"]}><ClinicProfile /></Protected>} />
      <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
      <Route path="/audit" element={<Protected allowedRoles={["doctor", "admin", "nurse", "reception"]}><AuditLog /></Protected>} />
      <Route path="/pitch" element={<Protected><PitchPage /></Protected>} />
      <Route path="/p/pitch" element={<PublicPitch />} />
      <Route path="/reception" element={<Protected allowedRoles={["doctor","admin","reception"]}><Reception /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
